// Pure serialiser for the quarterly review export. No React, no fetch — this
// module is the intermediate representation the /quarterly detail page reads
// AND the thing an auditor takes away as a document. Building it first (per
// the task brief) forces the response shape to be complete before any JSX
// exists.
//
// The two text formats (markdown, Confluence storage format) render from one
// neutral section list rather than being two independent serialisers, so
// they cannot drift the way two hand-maintained emitters would.

// ---------------------------------------------------------------------------
// Response shapes. These mirror the GET /api/quarterly/{quarter} contract
// documented in task-4-report.md (corrected section) — the API package's
// QuarterlyReview type isn't shared across the workspace boundary, so this is
// a deliberate, minimal local mirror, the same way Suppression/
// PolicyVersionListItem mirror their handlers elsewhere in this package.
// ---------------------------------------------------------------------------

export interface RevertArm {
  merged: number;
  reverted: number;
  /** null when `merged` is 0 — a missing denominator, not a checked zero.
   *  Rendering it as 0% would be an affirmative false claim. */
  rate: number | null;
}

/** 'legacy' covers REVIEW# rows persisted before the completeness-tagging
 *  change (Task 1) — production carries these until the Q2 regeneration. */
export type CompletenessCategory = 'structural' | 'data_quality' | 'legacy';

export interface CompletenessEntry {
  category: CompletenessCategory;
  text: string;
}

export interface QuarterlyIncident {
  title?: string;
  occurred_at?: string;
  severity?: string | null;
  source_url?: string;
  [key: string]: unknown;
}

export interface QuarterlyPolicyVersion {
  version?: string;
  pushed_at?: string;
  author_login?: string;
  commit_message?: string;
  pr_url?: string | null;
  pr_number?: number | null;
  [key: string]: unknown;
}

export interface QuarterlyBaselineChange {
  org?: string;
  repo?: string;
  computed_at?: string;
  [key: string]: unknown;
}

export interface QuarterlyReviewRow {
  pk: string;
  sk: string;
  quarter: string;
  org: string;
  period_start: string;
  period_end: string;
  generated_at: string;
  revert_rates: { ai_approved: RevertArm; human_approved: RevertArm };
  override_usage: { invocations: number; prs_reviewed: number; rate: number | null };
  incidents: QuarterlyIncident[];
  policy_versions: QuarterlyPolicyVersion[];
  baseline_changes: QuarterlyBaselineChange[];
  /** Counts-only stub on this row (sampled always []) — the per-candidate
   *  snapshots live in the top-level samples[] rows, not here. */
  drift_samples: unknown[];
  completeness: CompletenessEntry[];
  completeness_legacy: boolean;
  superseded: boolean;
}

export interface DriftCandidate {
  comment_id: string;
  org: string;
  repo: string;
  pr: number;
  reviewer: string;
  /** Snapshot AS REVIEWED — never re-fetched live. The stored row is the
   *  evidence; a 2029 render must match what was signed. */
  body_excerpt: string;
  url: string;
  ts: string;
  acted_on?: boolean | null;
}

export interface QuarterlySampleRow {
  pk: string;
  sk: string;
  quarter: string;
  org: string;
  reviewer: string;
  generated_at: string;
  requested: number;
  available: number;
  sampled: DriftCandidate[];
  superseded: boolean;
}

export interface QuarterlySignoffRow {
  pk: string;
  sk: string;
  quarter: string;
  org: string;
  signed_by: string;
  signed_by_role: string;
  signed_at: string;
  attestation: string;
  report_generated_at: string;
  revision: number;
}

export interface QuarterlyNoteRow {
  pk: string;
  sk: string;
  quarter: string;
  org: string;
  author: string;
  author_role: string;
  created_at: string;
  body: string;
}

export interface QuarterlyManifest {
  generated_at: string;
  orgs: Array<{ org: string; reviewers: string[] }>;
  complete: boolean;
  /** True when the generation that wrote this manifest was scoped to an
   *  explicit org list, so `orgs` is NOT the quarter's authoritative contents
   *  list. Optional so a response from an older API deserialises. */
  orgs_subset?: boolean;
}

export interface QuarterlyDetailResponse {
  quarter: string;
  period_start: string;
  period_end: string;
  manifest_present: boolean;
  manifest: QuarterlyManifest | null;
  reviews: QuarterlyReviewRow[];
  samples: QuarterlySampleRow[];
  signoffs: { current: QuarterlySignoffRow[]; history: QuarterlySignoffRow[] };
  notes: QuarterlyNoteRow[];
  reviewer_diff:
    | Record<string, { missing_samples: string[]; unexpected_samples: string[]; absent_from_manifest?: boolean }>
    | null;
  /** Per-org: the current sign-off's `report_generated_at` does not match the
   *  org's current `REVIEW#` row `generated_at` — i.e. the quarter was
   *  regenerated after it was signed, so the signature covers figures that are
   *  no longer the ones displayed. Optional so a response from an older API
   *  deserialises (see signatureStaleFor for the fallback). */
  signature_stale?: Record<string, boolean>;
}

export interface QuarterlyOrgSignoffState {
  org: string;
  signed: boolean;
  signed_by: string | null;
  signed_by_role: string | null;
  signed_at: string | null;
}

export interface QuarterlyIndexItem {
  quarter: string;
  period_start: string;
  period_end: string;
  generated_at: string;
  manifest_present: boolean;
  /** Every REVIEW# row on this quarter uses the pre-categorisation
   *  `completeness` shape — the quarter PREDATES the manifest convention
   *  rather than having a generation that failed part-way. Optional so a
   *  response from an older API deserialises as "not known to be legacy",
   *  which falls back to the previous wording rather than to a softer claim
   *  that might not be true. */
  completeness_legacy?: boolean;
  orgs: QuarterlyOrgSignoffState[];
}

// ---------------------------------------------------------------------------
// Formatting helpers. Shared between this serialiser and the two route
// components so the "null renders as X, never Y" invariants can't drift
// between the exported document and the on-screen render.
// ---------------------------------------------------------------------------

/** null → "not available" (a missing denominator, never a healthy zero).
 *  A checked zero rate (denominator > 0, numerator 0) legitimately renders
 *  "0.0%" — that is a real observation, not the defect this guards against. */
export function formatRate(rate: number | null): string {
  return rate === null ? 'not available' : `${(rate * 100).toFixed(1)}%`;
}

/** acted_on is populated from a single field that is null for every row
 *  today (D4) — rendering that as a definite "not acted on" would be the
 *  same defect as rendering a null rate as 0%. */
export function formatActedOn(acted_on: boolean | null | undefined): string {
  if (acted_on === true) return 'Acted on';
  if (acted_on === false) return 'Not acted on';
  return 'Unknown';
}

/** Auditor-facing evidence, print/PDF included, so the stamp is explicitly UTC
 *  rather than the viewer's locale: `toLocaleDateString`/`toLocaleTimeString`
 *  made the same attestation read differently on two machines and could not be
 *  reconciled against the stored `signed_at`, CloudWatch or Slack, all of which
 *  are UTC. An unparseable value echoes through verbatim — "Invalid Date" in a
 *  compliance artifact hides the input that produced it. */
export function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

export function structuralEntries(entries: CompletenessEntry[]): CompletenessEntry[] {
  return entries.filter(e => e.category === 'structural');
}
export function dataQualityEntries(entries: CompletenessEntry[]): CompletenessEntry[] {
  return entries.filter(e => e.category === 'data_quality');
}
export function legacyEntries(entries: CompletenessEntry[]): CompletenessEntry[] {
  return entries.filter(e => e.category === 'legacy');
}

/** "Clean quarter" keys ONLY on the count of data_quality entries — the
 *  structural entries are unconditional disclosures about the artifact's own
 *  shape, so completeness.length is never a valid clean-quarter test (it is
 *  never 0). A legacy-categorised row is never clean, regardless of count:
 *  it predates the categorisation that this test depends on. */
export function isCleanQuarter(review: Pick<QuarterlyReviewRow, 'completeness' | 'completeness_legacy'>): boolean {
  if (review.completeness_legacy) return false;
  return dataQualityEntries(review.completeness).length === 0;
}

/** Exactly one `REVIEW#` row can exist per (quarter, org): `keys.ts`'s
 *  `quarterlyReviewSk` is `REVIEW#<org>` — no suffix — so it is overwritten
 *  on every re-run, not appended. There is therefore nothing to pick
 *  between; `reviews` never holds more than one row for a given `org`. This
 *  is the ONE place that lookup happens, so the route (on-screen render)
 *  and this module (the export) cannot independently invent two rules that
 *  disagree — a real risk if the key scheme ever grows a suffix and one of
 *  the two call sites doesn't get updated. A `superseded: true` row IS
 *  returned here — superseded means "generated before the current
 *  manifest", not "not the one row that exists" — callers are responsible
 *  for marking it, never for hiding or skipping it. */
export function selectReview(reviews: QuarterlyReviewRow[], org: string): QuarterlyReviewRow | null {
  return reviews.find(r => r.org === org) ?? null;
}

/** Is this org's current sign-off bound to a report version that is no longer
 *  the one on display?
 *
 *  `report_generated_at` is stamped from the `REVIEW#` row's `generated_at` at
 *  the moment of signing, precisely so a signature binds to a version. Until
 *  this function existed, nothing anywhere read it — so a quarter regenerated
 *  after signing rendered "Signed by X … revision N" beside completely
 *  different figures, and the exported document (the thing that reaches the
 *  compliance record) did the same.
 *
 *  `superseded` does NOT cover this: a regeneration rewrites the manifest AND
 *  the `REVIEW#` row to the same new `generated_at`, so nothing is older than
 *  the manifest and every `superseded` flag is false.
 *
 *  Prefers the API's own `signature_stale` map — the server is the single
 *  authority, and the gauge keys off the same comparison — and falls back to
 *  computing it locally so a portal deployed ahead of the API still shows the
 *  banner rather than silently showing nothing. This is the ONE place the rule
 *  lives, shared by the on-screen render and the export, for the same reason
 *  selectReview is. */
export function signatureStaleFor(detail: QuarterlyDetailResponse, org: string): boolean {
  const fromApi = detail.signature_stale?.[org];
  if (typeof fromApi === 'boolean') return fromApi;
  const signoff = detail.signoffs.current.find(s => s.org === org) ?? null;
  if (!signoff) return false;
  const review = selectReview(detail.reviews, org);
  return review === null || signoff.report_generated_at !== review.generated_at;
}

/** Returns `url` only if it is an absolute `http:`/`https:` URL, else null.
 *
 *  Incident `source_url` values are internally ingested, so this is not a live
 *  vector — but it reaches the DOM as a bare `href`, and `javascript:` /
 *  `data:` in an href is a click away from script execution in the viewer's
 *  session. The export path already neutralises the same field by XML-escaping
 *  it; the rendered path had no equivalent. An allowlist (not a denylist) so a
 *  scheme nobody thought of is refused by default. A rejected URL renders as
 *  plain text — never dropped silently, since the value is evidence. */
export function safeHttpUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string' || url.length === 0) return null;
  try {
    const scheme = new URL(url).protocol;
    return scheme === 'http:' || scheme === 'https:' ? url : null;
  } catch {
    // Not an absolute URL at all (relative, or unparseable).
    return null;
  }
}

// ---------------------------------------------------------------------------
// Shared disclosure wording. Each of these exists because the on-screen render
// and the exported document said DIFFERENT things about the same state — and
// the export is the surface that leaves the building, so a disclosure the
// screen makes and the document does not is no disclosure at all. One source
// of wording, both surfaces, for the same reason selectReview and
// signatureStaleFor are single-sourced.
// ---------------------------------------------------------------------------

export interface Disclosure {
  /** Sentence-one claim: the banner headline / the export's reason clause. */
  headline: string;
  /** The same claim reduced to a clause, for `Manifest: MISSING — <reason>.` */
  reason: string;
  /** The supporting detail both surfaces print under the headline. */
  detail: string;
}

/** Every `REVIEW#` row on the quarter uses the pre-categorisation
 *  `completeness` shape, so an absent manifest means the quarter PREDATES the
 *  manifest convention rather than that a generation failed part-way. A
 *  partition with any tagged row is a genuinely incomplete or partially
 *  regenerated generation and keeps the hard wording. */
export function allReviewsLegacy(detail: Pick<QuarterlyDetailResponse, 'reviews'>): boolean {
  return detail.reviews.length > 0 && detail.reviews.every(r => r.completeness_legacy);
}

/** Wording for a manifest-absent quarter. The UI already softened this for a
 *  legacy quarter; the export did not, and emitted "this generation did not
 *  complete. This artifact may be partial." for EVERY manifest-absent quarter
 *  including legacy ones whose generation completed fine — a factually
 *  incorrect statement in the header of a compliance record. */
export function manifestAbsentDisclosure(legacy: boolean): Disclosure {
  return legacy
    ? {
      headline: 'No manifest — this quarter predates the manifest convention.',
      reason: 'this quarter predates the manifest convention',
      detail:
        'Its generation completed; the QUARTER_MANIFEST row and categorised completeness notes ' +
        'did not exist yet. Regenerate the quarter to complete it. Sign-off stays disabled ' +
        'until then.',
    }
    : {
      headline: 'Manifest missing — generation did not complete for this quarter.',
      reason: 'this generation did not complete',
      detail: 'This artifact may be partial. Treat every figure below with that in mind.',
    };
}

/** Wording for a manifest written by a run scoped to an explicit `orgs` list.
 *
 *  Two defects met here. First, the export never emitted this state at all:
 *  re-invoking the aggregator with an org subset is a DOCUMENTED recovery
 *  operation, and doing so then signing the one listed org produced an
 *  exported document that read as a complete, signed Clause 9.1 review with no
 *  indication another org was never reviewed.
 *
 *  Second, the remedy the banner gave was wrong. The flag is stamped on ANY
 *  explicit-`orgs` run, including one that lists every org, so an operator
 *  told to "regenerate the quarter over every org" would re-invoke with
 *  `{"quarter":"…","orgs":["rewindio","rewindio-labs"]}`, re-stamp the flag,
 *  and watch the overdue gauge keep climbing while apparently doing exactly
 *  what the screen said. The payload is therefore named explicitly. */
export function orgsSubsetDisclosure(quarter: string): Disclosure {
  const headline = "Partial regeneration — this quarter's manifest covers only some orgs.";
  return {
    headline,
    reason: "this quarter's manifest covers only some orgs",
    detail:
      'It was written by a run scoped to an explicit org list, so it is not an authoritative ' +
      'contents list for the quarter. To complete it, re-invoke AggregateQuarterlyReviewFn with ' +
      `{"quarter":"${quarter}"} and NO "orgs" field — any explicit "orgs" list re-stamps this ` +
      'flag, even one that names every org. The overdue gauge counts this quarter as unsigned ' +
      'until then.',
  };
}

// ---------------------------------------------------------------------------
// Neutral intermediate section list.
// ---------------------------------------------------------------------------

export interface ExportTable {
  headers: string[];
  rows: string[][];
}

export type ExportBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'table'; table: ExportTable };

export interface ExportSection {
  heading: string;
  blocks: ExportBlock[];
}

function p(text: string): ExportBlock {
  return { type: 'paragraph', text };
}
function list(items: string[]): ExportBlock {
  return { type: 'list', items };
}
function table(headers: string[], rows: string[][]): ExportBlock {
  return { type: 'table', table: { headers, rows } };
}

function describeIncident(i: QuarterlyIncident): string {
  const title = typeof i['title'] === 'string' ? i['title'] : 'Untitled incident';
  const occurred = typeof i['occurred_at'] === 'string' ? i['occurred_at'] : 'unknown time';
  const severity = typeof i['severity'] === 'string' ? i['severity'] : 'unknown severity';
  return `${title} (${severity}) — occurred ${occurred}`;
}

function describePolicyVersion(v: QuarterlyPolicyVersion): string {
  const version = typeof v['version'] === 'string' ? v['version'] : 'unknown version';
  const pushedAt = typeof v['pushed_at'] === 'string' ? v['pushed_at'] : 'unknown date';
  const message = typeof v['commit_message'] === 'string' ? v['commit_message'] : '';
  return `${version} — ${pushedAt}${message ? `: ${message}` : ''}`;
}

function describeBaselineChange(b: QuarterlyBaselineChange): string {
  const repo = typeof b['repo'] === 'string' ? b['repo'] : 'unknown repo';
  const computedAt = typeof b['computed_at'] === 'string' ? b['computed_at'] : 'unknown date';
  return `${repo} — re-baselined ${computedAt}`;
}

/** One reviewer's drift-sample entry. `available: 0` renders the honest
 *  reason rather than "no data" or a bare zero. Since the D3 ruling
 *  (2026-08-28) the control samples every comment a reviewer posted, not
 *  only those from reviewers that marked a finding substantive, so zero here
 *  means the reviewer posted nothing at all — this copy must say that and
 *  not the old advisory-only explanation, which is now false. Every sampled
 *  candidate renders its STORED `body_excerpt` — never re-fetched — with the
 *  live URL alongside as a convenience, and its `acted_on` state via
 *  formatActedOn. */
function driftSampleBlocks(s: QuarterlySampleRow): ExportBlock[] {
  const header = p(
    `${s.reviewer}: requested ${s.requested}, available ${s.available}` +
    (s.superseded ? ' [SUPERSEDED — replaced by a later generation]' : ''),
  );
  if (s.available === 0) {
    return [
      header,
      p(
        `This reviewer posted no comments at all this quarter. The sample draws on every comment ` +
        `a reviewer posted, substantive or advisory, so this is an absence of output — not a data gap.`,
      ),
    ];
  }
  return [
    header,
    table(
      ['PR', 'Comment (stored snapshot)', 'Acted on', 'Live link'],
      s.sampled.map(c => [String(c.pr), c.body_excerpt, formatActedOn(c.acted_on), c.url]),
    ),
  ];
}

/** Builds the neutral section list for one org's evidence within a quarter.
 *  Section order matches Luiz's Clause 9.1 mapping exactly (this ordering is
 *  policy, not a design preference), followed by the data-completeness band
 *  and the sign-off block. */
export function buildQuarterlyExportSections(detail: QuarterlyDetailResponse, org: string): ExportSection[] {
  const review = selectReview(detail.reviews, org);
  const samples = detail.samples
    .filter(s => s.org === org)
    .sort((a, b) => a.reviewer.localeCompare(b.reviewer));
  const currentSignoff = detail.signoffs.current.find(s => s.org === org) ?? null;
  const historySignoffs = detail.signoffs.history.filter(s => s.org === org);
  const notes = detail.notes.filter(n => n.org === org);

  const sections: ExportSection[] = [];

  const manifestAbsent = manifestAbsentDisclosure(allReviewsLegacy(detail));
  const subset = orgsSubsetDisclosure(detail.quarter);
  // Orgs that have evidence rows this quarter which the quarter's own manifest
  // does not list — the API's own `absent_from_manifest` flag. An empty diff
  // and an org that was never in the contents list are NOT the same state, and
  // only the second one means the manifest is not a complete contents list.
  const absentOrgs = Object.entries(detail.reviewer_diff ?? {})
    .filter(([, d]) => d.absent_from_manifest === true)
    .map(([o]) => o)
    .sort();

  sections.push({
    heading: `Quarterly Review — ${detail.quarter} (${org})`,
    blocks: [
      p(`Period: ${detail.period_start} to ${detail.period_end}`),
      p(
        detail.manifest_present
          ? `Manifest: generated at ${detail.manifest?.generated_at ?? 'unknown'}`
          : `Manifest: MISSING — ${manifestAbsent.reason}. ${manifestAbsent.detail}`,
      ),
      // UNCONDITIONAL, and deliberately not scoped to whether THIS org is one
      // of the missing ones: the on-screen banner was the only place the subset
      // state was disclosed, and the screen is the one surface that does not
      // leave the building. An exported document that reads as a complete
      // signed Clause 9.1 review while another org was never reviewed is
      // precisely the misstatement this control exists to remove.
      ...(detail.manifest?.orgs_subset === true ? [p(subset.headline), p(subset.detail)] : []),
      ...(absentOrgs.length > 0
        ? [
          p(
            'Orgs with review evidence on record this quarter that the manifest does NOT list — ' +
            'the manifest is not a complete contents list for this quarter:',
          ),
          list(absentOrgs.map(o => `${o} — has evidence rows but is absent from this quarter's manifest.`)),
        ]
        : []),
      ...(review?.superseded
        ? [p(`SUPERSEDED — generated ${review.generated_at}, older than this quarter's manifest.`)]
        : []),
    ],
  });

  sections.push({
    heading: 'Revert rates — AI-approved vs. human-approved',
    blocks: review
      ? [
        table(
          ['Arm', 'Merged', 'Reverted', 'Rate'],
          [
            ['AI-approved', String(review.revert_rates.ai_approved.merged), String(review.revert_rates.ai_approved.reverted), formatRate(review.revert_rates.ai_approved.rate)],
            ['Human-approved', String(review.revert_rates.human_approved.merged), String(review.revert_rates.human_approved.reverted), formatRate(review.revert_rates.human_approved.rate)],
          ],
        ),
      ]
      : [p('No review data for this org.')],
  });

  // `!review` FIRST. "No incidents linked this quarter." is an affirmative
  // clean claim, and a null review row produced it with no data behind it —
  // reassurance backed by nothing. The sibling sections (revert rates,
  // override usage) already say "No review data for this org." in exactly this
  // situation; this one is reachable for any org listed in the manifest whose
  // REVIEW# row has not been written yet, or was written by an older partial
  // run.
  sections.push({
    heading: 'Production incidents linked to AI-approved PRs',
    blocks: !review
      ? [p('No review data for this org.')]
      : review.incidents.length > 0
        ? [list(review.incidents.map(describeIncident))]
        : [p('No incidents linked this quarter.')],
  });

  sections.push({
    heading: 'Engineer override usage',
    blocks: review
      ? [
        table(
          ['Invocations', 'PRs reviewed', 'Rate'],
          [[String(review.override_usage.invocations), String(review.override_usage.prs_reviewed), formatRate(review.override_usage.rate)]],
        ),
      ]
      : [p('No review data for this org.')],
  });

  sections.push({
    heading: 'Drift sample — per reviewer, with acted-on state',
    blocks: samples.length > 0
      ? samples.flatMap(driftSampleBlocks)
      : [p('No drift samples recorded.')],
  });

  sections.push({
    heading: 'Baseline and policy changes during the period',
    blocks: [
      ...(review && review.policy_versions.length > 0
        ? [list(review.policy_versions.map(describePolicyVersion))]
        : [p('No policy version changes recorded this period.')]),
      ...(review && review.baseline_changes.length > 0
        ? [list(review.baseline_changes.map(describeBaselineChange))]
        : [p('No baseline changes recorded this period.')]),
    ],
  });

  const structural = review ? structuralEntries(review.completeness) : [];
  const dataQuality = review ? dataQualityEntries(review.completeness) : [];
  const legacy = review ? legacyEntries(review.completeness) : [];
  const clean = review ? isCleanQuarter(review) : false;
  // "Clean quarter" is an affirmative claim and must be gated on
  // isCleanQuarter(), never inferred from dataQuality.length alone: a
  // legacy-categorised row has zero data_quality entries BY CONSTRUCTION
  // (its notes are all category 'legacy'), so a naive "list if present, else
  // say clean" falls straight into claiming a legacy quarter is clean. Each
  // list also gets its own label so an auditor reading the document can tell
  // this quarter's problems (data_quality) apart from the artifact's own
  // permanent shape (structural) — the two were previously emitted back to
  // back with no way to distinguish them.
  const completenessBlocks: ExportBlock[] = [];
  if (!review) {
    completenessBlocks.push(p('No review data for this org — completeness cannot be assessed.'));
  } else if (review.completeness_legacy) {
    completenessBlocks.push(p('This quarter predates completeness categorisation — regenerate it.'));
    completenessBlocks.push(p('Completeness cannot be assessed — this quarter predates categorisation.'));
    if (legacy.length > 0) completenessBlocks.push(list(legacy.map(e => e.text)));
  } else if (clean) {
    completenessBlocks.push(p('Clean quarter — no data-quality issues reported.'));
  } else {
    completenessBlocks.push(p('Data-quality notes for this quarter:'));
    completenessBlocks.push(list(dataQuality.map(e => e.text)));
  }
  if (structural.length > 0) {
    completenessBlocks.push(p("About this report — describes the artifact's own shape, not this quarter's data:"));
    completenessBlocks.push(list(structural.map(e => e.text)));
  }
  sections.push({ heading: 'Data completeness', blocks: completenessBlocks });

  // The signature-to-version binding, printed in the document an auditor
  // takes away. This block must never present a stale signature as current:
  // the export IS the compliance record, and "Signed by X" over figures X
  // never saw is the precise misstatement this whole control exists to
  // remove. So when the two versions differ, the report_generated_at the
  // signature covers is printed ALONGSIDE the current generated_at, with the
  // mismatch stated in words — not left for the reader to spot by comparing
  // two timestamps.
  const signatureStale = currentSignoff !== null && signatureStaleFor(detail, org);
  sections.push({
    heading: 'Sign-off',
    blocks: [
      currentSignoff
        ? p(
          `Signed by ${currentSignoff.signed_by} (${currentSignoff.signed_by_role}) at ` +
          `${currentSignoff.signed_at}, revision ${currentSignoff.revision}. ` +
          `Attestation: "${currentSignoff.attestation}"`,
        )
        : p('Not signed off.'),
      ...(currentSignoff
        ? [p(
          signatureStale
            ? `SIGNATURE DOES NOT COVER THIS REPORT VERSION. The sign-off binds to the report ` +
              `generated at ${currentSignoff.report_generated_at}, but the report shown above was ` +
              `generated at ${review ? review.generated_at : 'unknown (no report row on record)'}. ` +
              `The quarter was regenerated after it was signed, so the figures in this document ` +
              `have NOT been attested to. It must be reviewed and re-signed.`
            : `Signature covers the report generated at ${currentSignoff.report_generated_at}, which ` +
              `matches the report shown above.`,
        )]
        : []),
      ...(historySignoffs.length > 0
        ? [list(historySignoffs.map(s => `${s.signed_at} — ${s.signed_by} (revision ${s.revision})`))]
        : []),
    ],
  });

  if (notes.length > 0) {
    sections.push({
      heading: 'Notes',
      blocks: [list(notes.map(n => `${n.created_at} — ${n.author}: ${n.body}`))],
    });
  }

  return sections;
}

// ---------------------------------------------------------------------------
// Renderers. Both read the same section list — this is deliberate: two
// independently-written serialisers is how the two formats would drift.
// ---------------------------------------------------------------------------

/** Cell-level escape for a markdown table cell.
 *
 *  The backslash pass MUST come first. Escaping `|` before `\` turns a cell
 *  containing the literal two characters `\|` into `\\|`, which renders as a
 *  literal backslash followed by a REAL, unescaped cell break — the escape
 *  defeating itself and letting a value split its own row.
 *
 *  Every line ending is neutralised, not just LF: CommonMark treats a bare CR
 *  as a line ending too, so `\r` alone would end the table row.
 *
 *  The leading `=`/`+`/`-`/`@`/tab/CR guard is a PASTE-INTO-SPREADSHEET
 *  defence, not a markdown concern — neither markdown nor Confluence storage
 *  format evaluates formulas. Attacker-influenced values reach cells
 *  (`body_excerpt`, drift-comment URLs) and an auditor pasting an exported
 *  table into Excel or Sheets is plausible enough that the one-line guard is
 *  worth a leading apostrophe in the rare cell that trips it. */
/** `<` is escaped to the HTML entity `&lt;` here too, for the same reason it
 *  is escaped in `escapeMdBlock` below: `body_excerpt` and `c.url` are
 *  client-influenced strings that land in a table cell, and a markdown
 *  renderer that permits raw inline HTML inside a table cell (GitHub's does)
 *  will happily turn a cell value like `<img src=x onerror=alert(1)>` or a
 *  fake `<a href=...>` into live markup in the exported document an auditor
 *  reads. `>` alone is inert without a preceding literal `<`, so — as in
 *  `escapeMdBlock` — only `<` needs neutralising. Kept as an HTML-entity
 *  escape, not backslash, for the same reason: `\<` is not a markdown escape
 *  for HTML tag context, so it would do nothing in an HTML-permitting
 *  renderer. */
function escapeMdCell(s: string): string {
  let out = s
    .replace(/\\/g, '\\\\')
    .replace(/\|/g, '\\|')
    .replace(/</g, '&lt;')
    .replace(/\r\n|[\r\n]/g, ' ');
  if (/^[=+\-@\t\r]/.test(out)) out = `'${out}`;
  return out;
}

/** Block-level escape for every free-form string the markdown emitter pushes
 *  as its OWN line (headings, paragraphs, list items).
 *
 *  This is the fix for a real forgery vector, and it lives at the EMITTER on
 *  purpose. Escaping was previously applied at exactly one call site — the
 *  table-row loop — while `paragraph` and `list` pushed text verbatim. Several
 *  free-form, newline-permitting, client-supplied fields reach those two
 *  emitters: note bodies (`z.string().min(1).max(4000)`, no charset
 *  restriction), the sign-off `attestation`, and ingested incident titles and
 *  git commit messages (multi-line by nature).
 *
 *  Because the Notes section is emitted LAST, a `triager` — the LOWEST write
 *  privilege on this feature, which may add notes but explicitly may NOT sign
 *  off — could put `\n\n## Sign-off\n\nSigned by <name> (superAdmin) …` in a
 *  note body and make a forged sign-off block the final thing in the compliance
 *  document, forging the single fact this control exists to establish and
 *  undetectably from the artifact itself.
 *
 *  Field-by-field escaping is what failed here: a field added later would
 *  arrive unescaped again. So the rule is at the point of emission, and the
 *  Confluence emitter's `escapeXml` is applied the same way — identical
 *  section list, same guarantee in both renderers.
 *
 *  Three passes: collapse every line ending to a space (nothing can open a new
 *  block-level construct), HTML-entity-escape every `<` (see below), then
 *  backslash-escape a leading block marker (`#` heading, `>` quote, `-`/`*`/
 *  `+` bullet, `=` setext underline, `N.` ordered item) so the string cannot
 *  become a construct on the line it already owns.
 *
 *  The `<` pass closes a second forgery door the markdown-construct escaping
 *  above does not touch. A note body of
 *  `<h2>Sign-off</h2><p>Signed by ... (superAdmin) ...</p>` contains no `#`,
 *  `>`, `-`, `*`, `+`, `=` or ordered-list marker at the START of the string —
 *  the whole point of the attack is that it needs none — so it passed the
 *  construct escaping above untouched. Markdown renderers that permit raw
 *  inline HTML (GitHub, most wikis, Confluence's markdown import — precisely
 *  how this export is expected to be consumed) render that as a real `<h2>`
 *  heading, forging the same fact via a different door. `>` is left alone: it
 *  has no special meaning without a preceding literal `<`, so escaping `<`
 *  alone is sufficient to defuse it, and an HTML-entity escape (not a
 *  backslash) is required because `\<` is not a markdown escape for HTML tag
 *  context and would render as a literal backslash followed by a live `<` in
 *  any renderer that honours raw HTML. */
export function escapeMdBlock(s: string): string {
  return s
    .replace(/\r\n|[\r\n]/g, ' ')
    .replace(/</g, '&lt;')
    .replace(/^([#>\-*+=]|\d+\.)/, '\\$1');
}

export function renderMarkdown(sections: ExportSection[]): string {
  const lines: string[] = [];
  for (const section of sections) {
    lines.push(`## ${escapeMdBlock(section.heading)}`, '');
    for (const block of section.blocks) {
      if (block.type === 'paragraph') {
        lines.push(escapeMdBlock(block.text), '');
      } else if (block.type === 'list') {
        for (const item of block.items) lines.push(`- ${escapeMdBlock(item)}`);
        lines.push('');
      } else {
        lines.push(`| ${block.table.headers.map(escapeMdCell).join(' | ')} |`);
        lines.push(`| ${block.table.headers.map(() => '---').join(' | ')} |`);
        for (const row of block.table.rows) lines.push(`| ${row.map(escapeMdCell).join(' | ')} |`);
        lines.push('');
      }
    }
  }
  return `${lines.join('\n').trimEnd()}\n`;
}

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Confluence storage format (XHTML). A thin second emitter over the same
 *  section list built above — required alongside markdown per the design
 *  spec (Task 11's Compliance Mapping correction points at this mirror
 *  flow), not an independently-maintained format. */
export function renderConfluenceStorage(sections: ExportSection[]): string {
  const parts: string[] = [];
  for (const section of sections) {
    parts.push(`<h2>${escapeXml(section.heading)}</h2>`);
    for (const block of section.blocks) {
      if (block.type === 'paragraph') {
        parts.push(`<p>${escapeXml(block.text)}</p>`);
      } else if (block.type === 'list') {
        parts.push(`<ul>${block.items.map(i => `<li>${escapeXml(i)}</li>`).join('')}</ul>`);
      } else {
        const head = `<tr>${block.table.headers.map(h => `<th>${escapeXml(h)}</th>`).join('')}</tr>`;
        const rows = block.table.rows.map(r => `<tr>${r.map(c => `<td>${escapeXml(c)}</td>`).join('')}</tr>`).join('');
        // Confluence storage format requires <thead> for a header row. With
        // <th> inside <tbody> it renders the header as an ordinary body row,
        // so every exported table reached the auditor with no column labels.
        parts.push(`<table><thead>${head}</thead><tbody>${rows}</tbody></table>`);
      }
    }
  }
  return parts.join('\n');
}

export function exportQuarterlyMarkdown(detail: QuarterlyDetailResponse, org: string): string {
  return renderMarkdown(buildQuarterlyExportSections(detail, org));
}

export function exportQuarterlyConfluence(detail: QuarterlyDetailResponse, org: string): string {
  return renderConfluenceStorage(buildQuarterlyExportSections(detail, org));
}
